import React, { createContext, useCallback, useMemo, useState } from 'react';
import type { OrderSearchRequest } from '@goodfynd/types';
import { addDays, addMinutes, DATE_RANGES, differenceInMinutes, endOfDay, startOfDay, tryFormat } from '@goodfynd/utils';
import { useOrdersReportApi } from '@hooks/api';
import { useReportCustomerOverview } from '@hooks/useReportCustomerOverview';
import { useReportPopularProducts } from '@hooks/useReportPopularProducts';
import { useReportTrend } from '@hooks/useReportTrend';
import { asCurrency } from '@utils/number-util';

import config from '../../../config';
import { OrderSort } from '../../../hooks/useOrderSearch';

import type {
  OrdersOverviewContextState,
  OrdersOverviewContextValue,
  OrdersOverviewDispatchContextValue,
  OrdersOverviewProviderProps,
} from './types';

const OrdersOverviewContext = createContext<
  OrdersOverviewContextValue | undefined
>(undefined);

const OrdersOverviewDispatchContext = createContext<
  OrdersOverviewDispatchContextValue | undefined
>(undefined);

export default function OrdersOverviewProvider({
  children,
  organizationId = '',
  vendorId = '',
}: OrdersOverviewProviderProps) {
  const ordersReportApi = useOrdersReportApi();
  const dateEnd = endOfDay(DATE_RANGES.Today.end);
  const dateStart = startOfDay(DATE_RANGES.Today.start);
  const [state, setState] = useState<OrdersOverviewContextState>({
    dateEnd,
    dateStart,
    dateEndCompare: endOfDay(dateStart),
    dateStartCompare: startOfDay(addDays(dateStart, -31)),
    orderItems: [],
    queryKey: config.queryKeys.ordersOverview,
    totalAmountLastPeriod: 0,
    totalTicketsLastPeriod: 0,
    trendLastPeriod: [],
  });

  const updateState = useCallback(
    (newState: Partial<OrdersOverviewContextState>) =>
      setState((curState) => ({ ...curState, ...newState })),
    [setState]
  );

  const baseQuery = useMemo<OrderSearchRequest>(
    () => ({
      merchantIds: vendorId,
      organizationIds: organizationId,
      paymentStatus: 'canceled,paid,paid_external,partially_refunded,refunded',
      rows: 0,
      sorts: OrderSort.PickupTimeAsc,
    }),
    [organizationId, vendorId]
  );

  const {
    data: customers,
    isError: customerOverviewHasError,
    isFetching: customerOverviewIsFetching,
  } = useReportCustomerOverview({
    onSuccess: async (_, toggleBusy) => {
      try {
        const customersLastPeriod = await ordersReportApi.customerOverview({
          ...baseQuery,
          pickupTimeEnd: dateEndCompare.toISOString(),
          pickupTimeStart: dateStartCompare.toISOString(),
        });

        updateState({
          customersLastPeriod,
        });
      } catch (error) {
        console.debug(error);
      } finally {
        toggleBusy(false);
      }
    },
    queryKey: [
      config.queryKeys.ordersReportCustomerOverview,
      state.dateEnd,
      state.dateStart,
    ],
    request: {
      ...baseQuery,
      pickupTimeEnd: endOfDay(state.dateEnd).toISOString(),
      pickupTimeStart: startOfDay(state.dateStart).toISOString(),
    },
  });

  const {
    count: totalTickets = 0,
    isError: trendHasError,
    isFetching: trendIsFetching,
    total: totalAmount = 0,
    trend = [],
  } = useReportTrend({
    onSuccess: async (_, toggleBusy) => {
      try {
        const {
          count: totalTicketsLastPeriod,
          total: totalAmountLastPeriod,
          trend: trendLastPeriod = [],
        } = await ordersReportApi.trend({
          ...baseQuery,
          pickupTimeEnd: dateEndCompare.toISOString(),
          pickupTimeStart: dateStartCompare.toISOString(),
        });

        updateState({
          totalAmountLastPeriod: asCurrency(totalAmountLastPeriod),
          totalTicketsLastPeriod,
          trendLastPeriod,
        });
      } catch (error) {
        console.debug(error);
      } finally {
        toggleBusy(false);
      }
    },
    queryKey: [
      config.queryKeys.ordersReportTrend,
      state.dateEnd,
      state.dateStart,
    ],
    request: {
      ...baseQuery,
      pickupTimeEnd: endOfDay(state.dateEnd).toISOString(),
      pickupTimeStart: startOfDay(state.dateStart).toISOString(),
    },
  });

  const {
    isError: popularProductsHasError,
    isFetching: popularProductsAreFetching,
    popularProducts,
    totalItems,
  } = useReportPopularProducts({
    queryKey: [
      config.queryKeys.ordersReportPopularProducts,
      state.dateEnd,
      state.dateStart,
    ],
    request: {
      ...baseQuery,
      pickupTimeEnd: endOfDay(state.dateEnd).toISOString(),
      pickupTimeStart: startOfDay(state.dateStart).toISOString(),
    },
  });

  const { dateEndCompare, dateStartCompare } = useMemo(() => {
    const durationInMinutes = differenceInMinutes(
      state.dateStart,
      state.dateEnd
    );

    const dateEndCompare = addMinutes(state.dateStart, -1);
    const dateStartCompare = addMinutes(dateEndCompare, durationInMinutes);
    return { dateEndCompare, dateStartCompare };
  }, [state.dateEnd, state.dateStart]);

  const value = useMemo(() => {
    const interval = differenceInMinutes(state.dateEnd, state.dateStart);
    const labelFormat =
      interval > 24 * 60
        ? config.dateFormats.monthDayShort
        : config.dateFormats.hourM;

    return {
      ...state,
      customers,
      customerOverviewHasError,
      customerOverviewIsFetching,
      organizationId,
      popularProducts,
      popularProductsAreFetching,
      popularProductsHasError,
      totalAmount,
      totalItems,
      totalTickets,
      trend,
      trendHasError,
      trendIsFetching,
      xAxisLabelLeft: tryFormat(state.dateStart, labelFormat).toLowerCase(),
      xAxisLabelRight: tryFormat(state.dateEnd, labelFormat).toLowerCase(),
      vendorId,
    };
  }, [
    customerOverviewHasError,
    customerOverviewIsFetching,
    customers,
    organizationId,
    popularProducts,
    popularProductsAreFetching,
    popularProductsHasError,
    state,
    totalAmount,
    totalItems,
    totalTickets,
    trend,
    trendHasError,
    trendIsFetching,
    vendorId,
  ]);

  return (
    <OrdersOverviewContext.Provider value={value}>
      <OrdersOverviewDispatchContext.Provider
        value={{
          changeDateRange: ({ start, end }) => {
            if (start && end) {
              updateState({
                dateEnd: endOfDay(end),
                dateStart: startOfDay(start),
              });
            }
          },
        }}
      >
        {children}
      </OrdersOverviewDispatchContext.Provider>
    </OrdersOverviewContext.Provider>
  );
}

export function useOrdersOverview() {
  const context = React.useContext(OrdersOverviewContext);
  if (context === undefined) {
    throw new Error(
      'The useOrdersOverview hook must be used within a OrdersOverviewProvider.'
    );
  }
  return context;
}

export function useOrdersOverviewDispatch() {
  const context = React.useContext(OrdersOverviewDispatchContext);
  if (context === undefined) {
    throw new Error(
      'The useOrdersOverviewDispatch hook must be used within a OrdersOverviewProvider.'
    );
  }
  return context;
}
